import { create } from "zustand";
import { loadSave, uniqueBingoNumbers, writeSave } from "../bingo-shared/storage";
import { fullDeck, pickRandom } from "./logic";
import { coerceHallThemeId, type HallThemeId } from "./themes";
import { resetPhaseClock } from "./timeline";
import { isHallDisplay } from "./role";

export type Phase = "idle" | "mixing" | "dropping" | "calling" | "seating" | "celebrating";

const STORAGE_KEY = "bingoCallerV2State";
export const MIX_SECONDS_DEFAULT = 1.5;
export const MIX_SECONDS_MIN = 0.5;
export const MIX_SECONDS_MAX = 5;

export function clampMixSeconds(n: number) {
  if (!Number.isFinite(n)) return MIX_SECONDS_DEFAULT;
  return Math.round(Math.min(MIX_SECONDS_MAX, Math.max(MIX_SECONDS_MIN, n)) * 10) / 10;
}

export type HallSyncPayload = {
  remaining: number[];
  called: number[];
  current: number | null;
  phase: Phase;
  theme: HallThemeId;
  mixSeconds: number;
  voiceRepeats: number;
  voicePause: boolean;
  quickCall: boolean;
  tapSkip: boolean;
  liteMode: boolean;
  displayLiteMode: boolean;
};

type HallState = {
  remaining: number[];
  called: number[];
  current: number | null;
  phase: Phase;
  soundEnabled: boolean;
  voiceEnabled: boolean;
  voiceRepeats: number;
  voicePause: boolean;
  quickCall: boolean;
  tapSkip: boolean;
  mixSeconds: number;
  theme: HallThemeId;
  trauma: number;
  liteMode: boolean;
  displayLiteMode: boolean;
  draw: () => void;
  skipToCall: () => void;
  replayLast: () => boolean;
  setPhase: (phase: Phase) => void;
  completeSeat: () => void;
  celebrate: () => void;
  endCelebrate: () => void;
  reset: () => void;
  setSoundEnabled: (value: boolean) => void;
  setVoiceEnabled: (value: boolean) => void;
  setVoiceRepeats: (n: number) => void;
  setVoicePause: (value: boolean) => void;
  setQuickCall: (value: boolean) => void;
  setTapSkip: (value: boolean) => void;
  setMixSeconds: (n: number) => void;
  setTheme: (theme: HallThemeId) => void;
  setLiteMode: (value: boolean) => void;
  setDisplayLiteMode: (value: boolean) => void;
  setSharedLiteMode: (value: boolean) => void;
  addTrauma: (amount: number) => void;
  decayTrauma: (dt: number) => void;
  hydrate: () => void;
};

export function selectPageLiteMode(s: { liteMode: boolean; displayLiteMode: boolean }) {
  return isHallDisplay() ? s.displayLiteMode : s.liteMode;
}

let syncPush: (() => void) | null = null;

export function bindHallSyncPush(fn: (() => void) | null) {
  syncPush = fn;
}

function persistSlice(s: HallState) {
  writeSave(STORAGE_KEY, {
    remaining: s.remaining,
    called: s.called,
    current: s.current,
    soundEnabled: s.soundEnabled,
    voiceEnabled: s.voiceEnabled,
    voiceRepeats: s.voiceRepeats,
    voicePause: s.voicePause,
    quickCall: s.quickCall,
    tapSkip: s.tapSkip,
    mixSeconds: s.mixSeconds,
    theme: s.theme,
    liteMode: s.liteMode,
    displayLiteMode: s.displayLiteMode,
  });
  syncPush?.();
}

function remainingFromCalled(called: number[]) {
  const used = new Set(called);
  return fullDeck().filter((n) => !used.has(n));
}

export const useHallStore = create<HallState>((set, get) => ({
  remaining: fullDeck(),
  called: [],
  current: null,
  phase: "idle",
  soundEnabled: true,
  voiceEnabled: true,
  voiceRepeats: 1,
  voicePause: false,
  quickCall: false,
  tapSkip: true,
  mixSeconds: MIX_SECONDS_DEFAULT,
  theme: "bubblegum",
  trauma: 0,
  liteMode: false,
  displayLiteMode: false,

  draw: () => {
    const { phase, remaining, quickCall } = get();
    if (phase !== "idle") return;
    const picked = pickRandom(remaining);
    if (!picked) return;
    const skip = quickCall;
    const nextPhase = skip ? "calling" : "mixing";
    set({
      remaining: picked.rest,
      current: picked.pick,
      phase: nextPhase,
      trauma: skip ? 0 : 0.15,
    });
    resetPhaseClock(nextPhase);
    persistSlice(get());
  },

  skipToCall: () => {
    const { phase, current, tapSkip } = get();
    if (!tapSkip || current == null) return;
    if (phase !== "mixing" && phase !== "dropping") return;
    set({ phase: "calling", trauma: 0 });
    resetPhaseClock("calling");
    persistSlice(get());
  },

  replayLast: () => {
    const { phase, current, called } = get();
    if (phase === "mixing" || phase === "dropping") return false;
    const num = current ?? called.at(-1) ?? null;
    if (num == null) return false;
    set({ current: num, phase: "calling", trauma: 0 });
    resetPhaseClock("calling");
    persistSlice(get());
    return true;
  },

  setPhase: (phase) => {
    set({ phase });
    resetPhaseClock(phase);
    syncPush?.();
  },

  completeSeat: () => {
    const { current, called } = get();
    if (current == null) {
      set({ phase: "idle" });
      syncPush?.();
      return;
    }
    const nextCalled = called.includes(current) ? called : [...called, current];
    set({ called: nextCalled, remaining: remainingFromCalled(nextCalled), phase: "idle" });
    persistSlice(get());
  },

  celebrate: () => {
    const { phase, current, remaining, called } = get();
    if (phase === "celebrating") return;

    if ((phase === "mixing" || phase === "dropping") && current != null) {
      const nextRemaining = remaining.includes(current) ? remaining : [...remaining, current];
      set({
        remaining: nextRemaining,
        current: null,
        phase: "celebrating",
        trauma: 0,
      });
      persistSlice(get());
      return;
    }

    if ((phase === "calling" || phase === "seating") && current != null) {
      const nextCalled = called.includes(current) ? called : [...called, current];
      set({
        called: nextCalled,
        remaining: remainingFromCalled(nextCalled),
        current,
        phase: "celebrating",
        trauma: 0,
      });
      persistSlice(get());
      return;
    }

    set({ phase: "celebrating" });
    syncPush?.();
  },
  endCelebrate: () => {
    set({ phase: "idle" });
    syncPush?.();
  },

  reset: () => {
    set({
      remaining: fullDeck(),
      called: [],
      current: null,
      phase: "idle",
      trauma: 0,
    });
    persistSlice(get());
  },

  setSoundEnabled: (value) => {
    set({ soundEnabled: value });
    persistSlice(get());
  },
  setVoiceEnabled: (value) => {
    set({ voiceEnabled: value });
    persistSlice(get());
  },
  setVoiceRepeats: (n) => {
    set({ voiceRepeats: Math.max(1, Math.min(5, n)) });
    persistSlice(get());
  },
  setVoicePause: (value) => {
    set({ voicePause: value });
    persistSlice(get());
  },
  setQuickCall: (value) => {
    set({ quickCall: value });
    persistSlice(get());
  },
  setTapSkip: (value) => {
    set({ tapSkip: value });
    persistSlice(get());
  },
  setMixSeconds: (n) => {
    set({ mixSeconds: clampMixSeconds(n) });
    persistSlice(get());
  },
  setTheme: (theme) => {
    set({ theme });
    persistSlice(get());
  },
  setLiteMode: (value) => {
    set({ liteMode: value });
    persistSlice(get());
  },
  setDisplayLiteMode: (value) => {
    set({ displayLiteMode: value });
    persistSlice(get());
  },
  setSharedLiteMode: (value) => {
    set({ liteMode: value, displayLiteMode: value });
    persistSlice(get());
  },
  addTrauma: (amount) => set((s) => ({ trauma: Math.min(1, s.trauma + amount) })),
  decayTrauma: (dt) =>
    set((s) => {
      if (s.trauma <= 0) return s;
      return { trauma: Math.max(0, s.trauma - dt * 1.85) };
    }),

  hydrate: () => {
    const parsed = loadSave(STORAGE_KEY, null, (raw) => raw);
    if (!parsed || typeof parsed !== "object") return;
    const data = parsed as Record<string, unknown>;
    const called = uniqueBingoNumbers(data.called);
    const current = typeof data.current === "number" && data.current >= 1 && data.current <= 75
      ? data.current
      : null;
    const seated = current && !called.includes(current) ? [...called, current] : called;
    set({
      called: seated,
      remaining: remainingFromCalled(seated),
      current: seated[seated.length - 1] ?? null,
      soundEnabled: typeof data.soundEnabled === "boolean" ? data.soundEnabled : true,
      voiceEnabled: typeof data.voiceEnabled === "boolean" ? data.voiceEnabled : true,
      voiceRepeats: typeof data.voiceRepeats === "number" ? Math.max(1, Math.min(5, data.voiceRepeats)) : 1,
      voicePause: typeof data.voicePause === "boolean" ? data.voicePause : false,
      quickCall: typeof data.quickCall === "boolean" ? data.quickCall : false,
      tapSkip: typeof data.tapSkip === "boolean" ? data.tapSkip : true,
      mixSeconds: typeof data.mixSeconds === "number" ? clampMixSeconds(data.mixSeconds) : MIX_SECONDS_DEFAULT,
      theme: coerceHallThemeId(data.theme),
      liteMode: typeof data.liteMode === "boolean" ? data.liteMode : false,
      displayLiteMode:
        typeof data.displayLiteMode === "boolean"
          ? data.displayLiteMode
          : typeof data.liteMode === "boolean"
            ? data.liteMode
            : false,
      phase: "idle",
    });
  },
}));
